import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as InstallIcon,
  Upload as PushIcon,
  Verified as OfficialIcon,
  AccountTree as WorkflowIcon,
  Extension as NodeIcon,
  Category as CategoryIcon,
  FilterList as FilterIcon,
  Star as StarIcon,
  Code as TriggerIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { registryService } from '../services/registry.service';
import { RegistryNode, RegistryWorkflow, NodeStatus, WorkflowStatus } from '../types/registry.types';
import { useAuth } from '../contexts/AuthContext';

// ── helpers ────────────────────────────────────────────────────────────────────
const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  working: 'success',
  beta: 'warning',
  wip: 'warning',
  deprecated: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  working: 'Working',
  beta: 'Beta',
  wip: 'WIP',
  deprecated: 'Deprecated',
};

// ── Push Node Dialog ───────────────────────────────────────────────────────────
interface PushNodeDialogProps {
  open: boolean;
  onClose: () => void;
  onPushed: () => void;
  orgNodeTypes: { id: string; name: string; display_name: string }[];
}

function PushNodeDialog({ open, onClose, onPushed, orgNodeTypes }: PushNodeDialogProps) {
  const [selectedId, setSelectedId] = useState('');
  const [nodeStatus, setNodeStatus] = useState<NodeStatus>('working');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePush = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await registryService.pushNode({
        node_type_id: selectedId,
        status: nodeStatus,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      enqueueSnackbar('Node published to registry', { variant: 'success' });
      onPushed();
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error || 'Failed to push node', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Publish Node to Registry</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Select Node</InputLabel>
          <Select value={selectedId} label="Select Node" onChange={e => setSelectedId(e.target.value)}>
            {orgNodeTypes.map(n => (
              <MenuItem key={n.id} value={n.id}>{n.display_name} <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({n.name})</Typography></MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select value={nodeStatus} label="Status" onChange={e => setNodeStatus(e.target.value as NodeStatus)}>
            <MenuItem value="working">Working</MenuItem>
            <MenuItem value="beta">Beta</MenuItem>
            <MenuItem value="deprecated">Deprecated</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Tags (comma-separated)"
          value={tags}
          onChange={e => setTags(e.target.value)}
          placeholder="e.g. shopify, ecommerce, webhook"
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handlePush} disabled={!selectedId || loading} variant="contained" startIcon={loading ? <CircularProgress size={16} /> : <PushIcon />}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Push Workflow Dialog ───────────────────────────────────────────────────────
interface PushWorkflowDialogProps {
  open: boolean;
  onClose: () => void;
  onPushed: () => void;
  orgWorkflows: { id: string; name: string }[];
}

function PushWorkflowDialog({ open, onClose, onPushed, orgWorkflows }: PushWorkflowDialogProps) {
  const [selectedId, setSelectedId] = useState('');
  const [wfStatus, setWfStatus] = useState<WorkflowStatus>('working');
  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePush = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await registryService.pushWorkflow({
        workflow_id: selectedId,
        status: wfStatus,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        category_override: category,
      });
      enqueueSnackbar('Workflow published to registry', { variant: 'success' });
      onPushed();
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error || 'Failed to push workflow', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Publish Workflow to Registry</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Select Workflow</InputLabel>
          <Select value={selectedId} label="Select Workflow" onChange={e => setSelectedId(e.target.value)}>
            {orgWorkflows.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select value={wfStatus} label="Status" onChange={e => setWfStatus(e.target.value as WorkflowStatus)}>
            <MenuItem value="working">Working</MenuItem>
            <MenuItem value="wip">Work In Progress</MenuItem>
            <MenuItem value="deprecated">Deprecated</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Category"
          value={category}
          onChange={e => setCategory(e.target.value)}
          placeholder="e.g. E-Commerce, CRM, Marketing"
          fullWidth
        />
        <TextField
          label="Tags (comma-separated)"
          value={tags}
          onChange={e => setTags(e.target.value)}
          placeholder="e.g. shopify, email, automation"
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handlePush} disabled={!selectedId || loading} variant="contained" startIcon={loading ? <CircularProgress size={16} /> : <PushIcon />}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Node Card ──────────────────────────────────────────────────────────────────
interface NodeCardProps {
  node: RegistryNode;
  onInstall: (node: RegistryNode) => void;
  installing: boolean;
  installed: boolean;
  isAdmin: boolean;
  onMarkOfficial: (node: RegistryNode) => void;
}

function NodeCard({ node, onInstall, installing, installed, isAdmin, onMarkOfficial }: NodeCardProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {node.is_official && (
        <Tooltip title="Official ODA node">
          <OfficialIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'primary.main', fontSize: 18 }} />
        </Tooltip>
      )}
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {node.is_trigger && <Chip label="Trigger" size="small" color="secondary" icon={<TriggerIcon />} />}
          <Chip label={STATUS_LABEL[node.status] || node.status} size="small" color={STATUS_COLOR[node.status] || 'default'} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
          {node.display_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
          {node.node_type}
        </Typography>
        {node.description && (
          <Typography variant="body2" color="text.secondary" sx={{
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
          }}>
            {node.description}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {node.category && <Chip label={node.category} size="small" icon={<CategoryIcon />} variant="outlined" />}
          {node.tags.slice(0, 3).map(tag => <Chip key={tag} label={tag} size="small" variant="outlined" />)}
        </Box>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {node.install_count} installs · {node.pushed_from_org || 'Unknown'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isAdmin && !node.is_official && (
            <Tooltip title="Mark as official">
              <IconButton size="small" onClick={() => onMarkOfficial(node)}>
                <StarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            variant={installed ? 'outlined' : 'contained'}
            color={installed ? 'success' : 'primary'}
            startIcon={installing ? <CircularProgress size={14} /> : installed ? <CheckIcon /> : <InstallIcon />}
            onClick={() => !installed && onInstall(node)}
            disabled={installing || installed}
            sx={installed ? { pointerEvents: 'none' } : {}}
          >
            {installed ? 'Installed' : 'Install'}
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
}

// ── Workflow Card ──────────────────────────────────────────────────────────────
interface WorkflowCardProps {
  workflow: RegistryWorkflow;
  onInstall: (wf: RegistryWorkflow) => void;
  installing: boolean;
  installed: boolean;
  isAdmin: boolean;
  onMarkOfficial: (wf: RegistryWorkflow) => void;
}

function WorkflowCard({ workflow, onInstall, installing, installed, isAdmin, onMarkOfficial }: WorkflowCardProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {workflow.is_official && (
        <Tooltip title="Official ODA workflow">
          <OfficialIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'primary.main', fontSize: 18 }} />
        </Tooltip>
      )}
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Chip label={STATUS_LABEL[workflow.status] || workflow.status} size="small" color={STATUS_COLOR[workflow.status] || 'default'} />
          <Chip label={`${workflow.node_count} nodes`} size="small" icon={<WorkflowIcon />} variant="outlined" />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
          {workflow.name}
        </Typography>
        {workflow.description && (
          <Typography variant="body2" color="text.secondary" sx={{
            mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
          }}>
            {workflow.description}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {workflow.category && <Chip label={workflow.category} size="small" icon={<CategoryIcon />} variant="outlined" />}
          {workflow.tags.slice(0, 3).map(tag => <Chip key={tag} label={tag} size="small" variant="outlined" />)}
        </Box>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {workflow.install_count} installs · {workflow.pushed_from_org || 'Unknown'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isAdmin && !workflow.is_official && (
            <Tooltip title="Mark as official">
              <IconButton size="small" onClick={() => onMarkOfficial(workflow)}>
                <StarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            variant={installed ? 'outlined' : 'contained'}
            color={installed ? 'success' : 'primary'}
            startIcon={installing ? <CircularProgress size={14} /> : installed ? <CheckIcon /> : <InstallIcon />}
            onClick={() => !installed && onInstall(workflow)}
            disabled={installing || installed}
            sx={installed ? { pointerEvents: 'none' } : {}}
          >
            {installed ? 'Installed' : 'Install'}
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export const RegistryPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = Array.isArray((user as any)?.permissions)
    ? (user as any).permissions.includes('*') || (user as any).permissions.includes('org.manage')
    : false;
  const isSystemAdmin = (user as any)?.role === 'system_admin';
  const [seeding, setSeeding] = useState(false);

  const [tab, setTab] = useState<'nodes' | 'workflows'>('nodes');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [nodes, setNodes] = useState<RegistryNode[]>([]);
  const [nodeCount, setNodeCount] = useState(0);
  const [nodeCategories, setNodeCategories] = useState<string[]>([]);
  const [workflows, setWorkflows] = useState<RegistryWorkflow[]>([]);
  const [workflowCount, setWorkflowCount] = useState(0);
  const [workflowCategories, setWorkflowCategories] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [installingNode, setInstallingNode] = useState<string | null>(null);
  const [installingWorkflow, setInstallingWorkflow] = useState<string | null>(null);
  const [installedNodeIds, setInstalledNodeIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('wg_registry_installed_nodes') || '[]')); } catch { return new Set(); }
  });
  const [installedWorkflowIds, setInstalledWorkflowIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('wg_registry_installed_workflows') || '[]')); } catch { return new Set(); }
  });

  const [pushNodeOpen, setPushNodeOpen] = useState(false);
  const [pushWorkflowOpen, setPushWorkflowOpen] = useState(false);

  // For push dialogs — org's own nodes/workflows
  const [orgNodes, setOrgNodes] = useState<{ id: string; name: string; display_name: string }[]>([]);
  const [orgWorkflows, setOrgWorkflows] = useState<{ id: string; name: string }[]>([]);

  const fetchNodes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await registryService.getNodes({
        q: search || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
      });
      setNodes(res.results);
      setNodeCount(res.count);
    } catch {
      setError('Failed to load registry nodes');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter]);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await registryService.getWorkflows({
        q: search || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
      });
      setWorkflows(res.results);
      setWorkflowCount(res.count);
    } catch {
      setError('Failed to load registry workflows');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter]);

  useEffect(() => {
    registryService.getNodeCategories().then(setNodeCategories).catch(() => {});
    registryService.getWorkflowCategories().then(setWorkflowCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'nodes') fetchNodes();
    else fetchWorkflows();
  }, [tab, fetchNodes, fetchWorkflows]);

  const markNodeInstalled = (id: string, nodeType?: string) => {
    setInstalledNodeIds((prev) => {
      const s = new Set(Array.from(prev)); s.add(id);
      try { localStorage.setItem('wg_registry_installed_nodes', JSON.stringify(Array.from(s))); } catch {}
      return s;
    });
    // Also persist the node_type so the workflow editor palette can show it
    if (nodeType) {
      try {
        const existing: string[] = JSON.parse(localStorage.getItem('wg_installed_node_types') || '[]');
        if (!existing.includes(nodeType)) {
          existing.push(nodeType);
          localStorage.setItem('wg_installed_node_types', JSON.stringify(existing));
        }
      } catch {}
    }
  };

  const markWorkflowInstalled = (id: string) => {
    setInstalledWorkflowIds((prev) => {
      const s = new Set(Array.from(prev)); s.add(id);
      try { localStorage.setItem('wg_registry_installed_workflows', JSON.stringify(Array.from(s))); } catch {}
      return s;
    });
  };

  const handleInstallNode = async (node: RegistryNode) => {
    setInstallingNode(node.id);
    try {
      const res = await registryService.installNode(node.id);
      if (res.created) {
        enqueueSnackbar(`"${node.display_name}" installed — it will appear in the workflow palette`, { variant: 'success' });
      } else {
        enqueueSnackbar(`"${node.display_name}" is already installed`, { variant: 'info' });
      }
      markNodeInstalled(node.id, node.node_type);
      fetchNodes();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error || 'Install failed', { variant: 'error' });
    } finally {
      setInstallingNode(null);
    }
  };

  const handleInstallWorkflow = async (wf: RegistryWorkflow) => {
    setInstallingWorkflow(wf.id);
    try {
      const res = await registryService.installWorkflow(wf.id);
      enqueueSnackbar(`"${res.name}" installed — open Workflows to use it`, { variant: 'success' });
      markWorkflowInstalled(wf.id);
      fetchWorkflows();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error || 'Install failed', { variant: 'error' });
    } finally {
      setInstallingWorkflow(null);
    }
  };

  const handleMarkNodeOfficial = async (node: RegistryNode) => {
    try {
      await registryService.markNodeOfficial(node.id);
      enqueueSnackbar(`"${node.display_name}" marked as official`, { variant: 'success' });
      fetchNodes();
    } catch {
      enqueueSnackbar('Failed to mark as official', { variant: 'error' });
    }
  };

  const handleMarkWorkflowOfficial = async (wf: RegistryWorkflow) => {
    try {
      await registryService.markWorkflowOfficial(wf.id);
      enqueueSnackbar(`"${wf.name}" marked as official`, { variant: 'success' });
      fetchWorkflows();
    } catch {
      enqueueSnackbar('Failed to mark as official', { variant: 'error' });
    }
  };

  const openPushNodes = async () => {
    try {
      const res = await fetch('/api/node-import/imported-nodes/active/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || ''}` },
      });
      const data = await res.json();
      setOrgNodes(Array.isArray(data) ? data.map((n: any) => ({ id: n.id, name: n.name, display_name: n.display_name })) : []);
    } catch {
      setOrgNodes([]);
    }
    setPushNodeOpen(true);
  };

  const openPushWorkflows = async () => {
    try {
      const res = await fetch('/api/workflows/?page_size=200', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || ''}` },
      });
      const data = await res.json();
      setOrgWorkflows((data.results || []).map((w: any) => ({ id: w.id, name: w.name })));
    } catch {
      setOrgWorkflows([]);
    }
    setPushWorkflowOpen(true);
  };

  const categories = tab === 'nodes' ? nodeCategories : workflowCategories;

  const handleSeedBuiltins = async () => {
    setSeeding(true);
    try {
      const res = await registryService.seedBuiltins();
      enqueueSnackbar(
        `Registry seeded: ${res.created} created, ${res.updated} updated (${res.total} total)`,
        { variant: 'success' }
      );
      fetchNodes();
      registryService.getNodeCategories().then(setNodeCategories).catch(() => {});
    } catch {
      enqueueSnackbar('Failed to seed registry', { variant: 'error' });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Node &amp; Workflow Library</Typography>
          <Typography variant="body2" color="text.secondary">
            Browse available nodes and workflow templates. Click <strong>Install</strong> to add to your workspace.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isSystemAdmin && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleSeedBuiltins}
              disabled={seeding}
              startIcon={seeding ? <CircularProgress size={14} /> : undefined}
            >
              {seeding ? 'Seeding…' : 'Seed Built-in Nodes'}
            </Button>
          )}
          {isAdmin && (
            <>
              <Button variant="outlined" startIcon={<PushIcon />} onClick={openPushNodes} size="small">
                Publish Node
              </Button>
              <Button variant="outlined" startIcon={<PushIcon />} onClick={openPushWorkflows} size="small">
                Publish Workflow
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearch(''); setCategoryFilter(''); setStatusFilter(''); }} sx={{ mb: 2 }}>
        <Tab
          value="nodes"
          label={
            <Badge badgeContent={nodeCount} color="primary" max={999}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 2 }}>
                <NodeIcon fontSize="small" /> Nodes
              </Box>
            </Badge>
          }
        />
        <Tab
          value="workflows"
          label={
            <Badge badgeContent={workflowCount} color="primary" max={999}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 2 }}>
                <WorkflowIcon fontSize="small" /> Workflows
              </Box>
            </Badge>
          }
        />
      </Tabs>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {categories.filter(Boolean).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="working">Working</MenuItem>
            {tab === 'nodes' && <MenuItem value="beta">Beta</MenuItem>}
            {tab === 'workflows' && <MenuItem value="wip">WIP</MenuItem>}
            <MenuItem value="deprecated">Deprecated</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Content */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : tab === 'nodes' ? (
        nodes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <NodeIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No nodes in the registry yet.</Typography>
            {isAdmin && <Typography variant="body2">Use "Publish Node" to add nodes from your org.</Typography>}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {nodes.map(node => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={node.id}>
                <NodeCard
                  node={node}
                  onInstall={handleInstallNode}
                  installing={installingNode === node.id}
                  installed={installedNodeIds.has(node.id)}
                  isAdmin={isAdmin}
                  onMarkOfficial={handleMarkNodeOfficial}
                />
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        workflows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <WorkflowIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No workflows in the registry yet.</Typography>
            {isAdmin && <Typography variant="body2">Use "Publish Workflow" to add workflows from your org.</Typography>}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {workflows.map(wf => (
              <Grid item xs={12} sm={6} md={4} key={wf.id}>
                <WorkflowCard
                  workflow={wf}
                  onInstall={handleInstallWorkflow}
                  installing={installingWorkflow === wf.id}
                  installed={installedWorkflowIds.has(wf.id)}
                  isAdmin={isAdmin}
                  onMarkOfficial={handleMarkWorkflowOfficial}
                />
              </Grid>
            ))}
          </Grid>
        )
      )}

      {/* Push Dialogs */}
      <PushNodeDialog
        open={pushNodeOpen}
        onClose={() => setPushNodeOpen(false)}
        onPushed={fetchNodes}
        orgNodeTypes={orgNodes}
      />
      <PushWorkflowDialog
        open={pushWorkflowOpen}
        onClose={() => setPushWorkflowOpen(false)}
        onPushed={fetchWorkflows}
        orgWorkflows={orgWorkflows}
      />
    </Box>
  );
};

export default RegistryPage;
